import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { getAllCurrencies, isValidCurrency } from './getCurrencyCode.mjs';



const CurrencyDropdown = ({
  value,
  onChange,
  onCurrencySelect,
  itemId: _itemId,
  isReadOnly = false,
  onAutoFetchRate = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get currencies once and memoize them
  const allCurrencies = useMemo(() => getAllCurrencies(), []);

  // Memoize filtered currencies to prevent unnecessary recalculations
  const filteredCurrencies = useMemo(() => {
    if (searchTerm) {
      return allCurrencies.filter(currency =>
        currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else {
      // Show popular currencies first when no search term
      const popularCurrencies = ['USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY'];
      const popular = allCurrencies.filter(c => popularCurrencies.includes(c.code));
      const others = allCurrencies.filter(c => !popularCurrencies.includes(c.code));
      return [...popular, ...others];
    }
  }, [searchTerm, allCurrencies]);

  // Update search term when value changes externally (only if different)
  useEffect(() => {
    if (value !== searchTerm) {
      setSearchTerm(value || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // searchTerm intentionally omitted to avoid infinite loop

  // Position dropdown
  const positionDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      // Keep the menu on screen. It is at least 250px wide, and on a phone
      // the currency field is a half-width cell in the right-hand column, so
      // anchoring it at the field's left edge ran it off the right side.
      const vw = document.documentElement.clientWidth || window.innerWidth;
      const width = Math.min(Math.max(rect.width, 250), vw - 16);
      const left = Math.min(Math.max(rect.left, 8), vw - 8 - width);
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 2,
        left: left + window.scrollX,
        width
      });
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value.toUpperCase();
    setSearchTerm(newValue);
    onChange(newValue);

    if (!isOpen) {
      setIsOpen(true);
      setTimeout(positionDropdown, 0);
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (isReadOnly) return;

    setIsOpen(true);
    setTimeout(positionDropdown, 0);
  };

  // Handle currency selection
  const handleCurrencySelect = (currency) => {
    setSearchTerm(currency.code);
    onChange(currency.code);
    setIsOpen(false);

    // Call the callback for currency selection
    if (onCurrencySelect) {
      onCurrencySelect(currency.code);
    }

    // Auto-fetch exchange rate if valid currency and callback provided
    if (onAutoFetchRate && isValidCurrency(currency.code)) {
      onAutoFetchRate(currency.code);
    }
  };

  // Handle blur with delay to allow clicks
  const handleBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        positionDropdown();
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isOpen]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="USD, EUR, etc."
        className={`w-full min-h-[44px] lg:min-h-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent uppercase ${isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
        readOnly={isReadOnly}
        maxLength={3}
      />

      {isOpen && !isReadOnly && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: dropdownPosition.width ? `${dropdownPosition.width}px` : '250px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 99999
          }}
        >
          {filteredCurrencies.length > 0 ? (
            <ul>
              {filteredCurrencies.map(currency => (
                <li
                  key={currency.code}
                  className="min-h-[44px] sm:min-h-0 px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex justify-between items-center"
                  onClick={() => handleCurrencySelect(currency)}
                  onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
                >
                  <span className="font-medium text-gray-800">{currency.code}</span>
                  <span className="text-sm text-gray-500">{currency.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-center text-gray-500">
              No currencies found
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default CurrencyDropdown;
